import { Capacitor } from '@capacitor/core';
import React, { useEffect, useState } from 'react';
import { GGJLogoMark } from '../Brand/GGJLogo';

interface MobileAppLoaderProps {
  /** 'splash' — full-screen cold-launch (respects light/dark theme)
   *  'auth'   — checking auth / signing in (respects current theme)
   */
  phase?: 'splash' | 'auth';
  /** Optional line below the title in 'auth' phase (omit for logo + progress only) */
  message?: string;
}

/**
 * Full-screen native splash / loading screen for the Android app.
 *
 * Phase "splash" — cold-launch look (respects light/dark theme for consistency)
 * Phase "auth"   — displayed while checking session / signing in, respects theme
 */
const MobileAppLoader: React.FC<MobileAppLoaderProps> = ({
  phase = 'splash',
  message = '',
}) => {
  const [dots, setDots] = useState([false, false, false]);

  /* Hide the native Capacitor splash screen once this React loader is visible.
   * This creates a seamless cross-fade: native splash → React loader → app.
   * Runs only once on mount. */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Small delay ensures the React component is painted before the splash fades
    const t = setTimeout(() => {
      import('@capacitor/splash-screen')
        .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 250 }))
        .catch(() => {});
    }, 80);
    return () => clearTimeout(t);
  }, []);

  /* staggered dot animation: 0ms → 200ms → 400ms */
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const cycle = () => {
      setDots([false, false, false]);
      timers.push(setTimeout(() => setDots([true, false, false]), 0));
      timers.push(setTimeout(() => setDots([true, true, false]), 200));
      timers.push(setTimeout(() => setDots([true, true, true]), 400));
    };
    cycle();
    const interval = setInterval(cycle, 1400);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(interval);
    };
  }, []);

  /* ── PHASE: SPLASH ───────────────────────────────────────────────
   * Background and icon container use theme variables so splash is
   * consistent with light/dark (light: white bg + purple icon; dark: dark bg + dark header-style icon).
   */
  if (phase === 'splash') {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--vscode-bg-primary)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          fontFamily: 'var(--ds-font-sans)',
          transition: 'background 0.3s',
        }}
      >
        {/* Entrance: logo + text scale and fade in (major-app style) */}
        <div
          style={{
            animation: 'ggj-splash-enter 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              animation: 'ggj-pulse 2s ease-in-out infinite 0.5s',
              marginBottom: 32,
            }}
          >
            <GGJLogoMark
              size={100}
              className="shadow-[var(--brand-cta-shadow,0_0_40px_rgba(108,71,255,0.55))]"
            />
          </div>

          <div
            style={{
              fontSize: 28,
              fontWeight: 900,
              color: 'var(--vscode-text-primary)',
              letterSpacing: '-0.03em',
              marginBottom: 8,
            }}
          >
            GoGrabJob
          </div>
          <div style={{ fontSize: 14, color: 'var(--vscode-text-tertiary)' }}>
            Your AI job search companion
          </div>
        </div>

        {/* Animated dots */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 56,
            animation: 'ggj-splash-enter 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both',
          }}
        >
          {dots.map((active, i) => (
            <div
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--vscode-primary, #8B80F9)',
                opacity: active ? 1 : 0.25,
                transition: 'opacity 0.15s ease',
              }}
            />
          ))}
        </div>

        <style>{`
          @keyframes ggj-splash-enter {
            from { opacity: 0; transform: scale(0.92); }
            to   { opacity: 1; transform: scale(1); }
          }
          @keyframes ggj-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50%       { transform: scale(1.07); opacity: 0.88; }
          }
        `}</style>
      </div>
    );
  }

  /* ── PHASE: AUTH ─────────────────────────────────────────────────
   * Reads the same CSS variables the web app uses — zero hardcoded colors.
   */

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--vscode-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontFamily: 'var(--ds-font-sans)',
        transition: 'background 0.3s',
      }}
    >
      {/* Logo + text: theme-aware (in sync with splash and launcher), entrance animation */}
      <div
        style={{
          animation: 'ggj-splash-enter 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <GGJLogoMark
          size={80}
          className="mb-6 shadow-[var(--brand-cta-shadow)]"
        />

        <div
          style={{
            fontSize: 22,
            fontWeight: 900,
            color: 'var(--vscode-text-primary)',
            letterSpacing: '-0.02em',
            marginBottom: message ? 4 : 40,
          }}
        >
          GoGrabJob
        </div>
        {message ? (
        <div
          style={{
            fontSize: 13,
            color: 'var(--vscode-text-tertiary)',
            marginBottom: 40,
          }}
        >
          {message}
        </div>
        ) : null}
      </div>

      {/* Progress bar — entrance + smooth animation */}
      <div
        style={{
          width: 200,
          height: 4,
          background: 'var(--brand-spinner-track)',
          borderRadius: 2,
          overflow: 'hidden',
          animation: 'ggj-splash-enter 0.35s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both',
        }}
      >
        <div
          style={{
            height: '100%',
            background: 'var(--brand-cta-bg)',
            borderRadius: 2,
            animation: 'ggj-progress 1.6s ease-in-out infinite',
          }}
        />
      </div>

      <style>{`
        @keyframes ggj-splash-enter {
          from { opacity: 0; transform: scale(0.94); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes ggj-progress {
          0%   { width: 12%; margin-left: 0; }
          50%  { width: 58%; margin-left: 21%; }
          100% { width: 12%; margin-left: 88%; }
        }
      `}</style>
    </div>
  );
};

export default MobileAppLoader;
